import {
  Component,
  DestroyRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthenticationService } from '../authentication.service';
import { AuthService } from '@app/@core/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { interval, Subscription } from 'rxjs';

export interface ForceResetDialogData {
  email: string;
}

function passwordStrengthValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v: string = control.value ?? '';
    if (!v) return null;
    const valid =
      v.length >= 8 &&
      /[A-Z]/.test(v) &&
      /[0-9]/.test(v) &&
      /[^A-Za-z0-9]/.test(v);
    return valid ? null : { weakPassword: true };
  };
}

function passwordMatchValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const newPw = group.get('newPassword')?.value;
    const confirmPw = group.get('confirmPassword')?.value;
    return newPw && confirmPw && newPw !== confirmPw
      ? { mismatch: true }
      : null;
  };
}

@Component({
  selector: 'app-force-reset-password-dialog',
  templateUrl: './force-reset-password-dialog.component.html',
  styleUrls: ['./force-reset-password-dialog.component.scss'],
  standalone: false,
})
export class ForceResetPasswordDialogComponent implements OnInit, OnDestroy {
  step = 1;

  otpControl = new FormControl('');
  otpConfig = { length: 6, allowNumbersOnly: true };

  passwordForm = new FormGroup(
    {
      newPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        passwordStrengthValidator(),
      ]),
      confirmPassword: new FormControl('', [Validators.required]),
    },
    { validators: [passwordMatchValidator()] },
  );

  passwordResetToken: string;
  showNew = false;
  showConfirm = false;

  loading = {
    sending: false,
    verifying: false,
    resetting: false,
    resending: false,
  };

  error: string | null = null;
  isTimerActive = false;
  timer: number;
  private timerSubscription: Subscription;

  constructor(
    private dialogRef: MatDialogRef<ForceResetPasswordDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ForceResetDialogData,
    private authenticationService: AuthenticationService,
    private authService: AuthService,
    private translateService: TranslateService,
    private router: Router,
  ) {}

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.stopTimer();
  }

  startReset(): void {
    this.loading.sending = true;
    this.error = null;
    this.authenticationService
      .sendVerification({ email: this.data.email })
      .pipe(
        finalize(() => (this.loading.sending = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          const expiryMs = res?.expiryTime - Date.now();
          const seconds = expiryMs > 0 ? Math.floor(expiryMs / 1000) : 60;
          this.startTimer(seconds);
          this.step = 2;
        },
        error: (err) => {
          this.error =
            err?.error?.message ??
            err?.error?.errors?.[0] ??
            'An error occurred. Please try again.';
        },
      });
  }

  verifyOtp(): void {
    if ((this.otpControl.value?.length ?? 0) < 6) return;
    this.loading.verifying = true;
    this.error = null;
    this.authenticationService
      .verifyEmail({ email: this.data.email, code: this.otpControl.value })
      .pipe(
        finalize(() => (this.loading.verifying = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.passwordResetToken = res.token;
          this.step = 3;
        },
        error: (err) => {
          this.error =
            err?.error?.message ??
            err?.error?.errors?.[0] ??
            'Invalid code. Please try again.';
        },
      });
  }

  resendOtp(): void {
    this.loading.resending = true;
    this.error = null;
    this.otpControl.reset('');
    this.authenticationService
      .sendVerification({ email: this.data.email })
      .pipe(
        finalize(() => (this.loading.resending = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          const expiryMs = res?.expiryTime - Date.now();
          const seconds = expiryMs > 0 ? Math.floor(expiryMs / 1000) : 60;
          this.startTimer(seconds);
        },
        error: (err) => {
          this.error =
            err?.error?.message ??
            err?.error?.errors?.[0] ??
            'An error occurred. Please try again.';
        },
      });
  }

  resetPassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    this.loading.resetting = true;
    this.error = null;
    this.authenticationService
      .resetPassword(
        { password: this.passwordForm.get('newPassword').value },
        this.passwordResetToken,
      )
      .pipe(
        finalize(() => (this.loading.resetting = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.dialogRef.close();
          this.authService
            .logout()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this.router.navigate(['/login'], { replaceUrl: true });
            });
        },
        error: (err) => {
          this.error =
            err?.error?.message ??
            err?.error?.errors?.[0] ??
            'An error occurred. Please try again.';
        },
      });
  }

  getTimerText(sec: number): string {
    if (sec > 60) {
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      return `${m} ${this.translateService.instant('minute(s)')} ${this.translateService.instant('and')} ${s} ${this.translateService.instant('second(s)')}`;
    }
    return `${sec} ${this.translateService.instant('second(s)')}`;
  }

  get newPasswordErrors(): string | null {
    const ctrl = this.passwordForm.get('newPassword');
    if (!ctrl.touched || !ctrl.errors) return null;
    if (ctrl.errors['required']) return 'Password is required';
    if (ctrl.errors['minlength']) return 'Minimum 8 characters required';
    if (ctrl.errors['weakPassword'])
      return 'Must include uppercase, number, and special character';
    return null;
  }

  get showMismatchError(): boolean {
    return (
      !!this.passwordForm.errors?.['mismatch'] &&
      this.passwordForm.get('confirmPassword').touched
    );
  }

  private startTimer(duration: number): void {
    this.stopTimer();
    this.isTimerActive = true;
    this.timer = duration;
    this.timerSubscription = interval(1000).subscribe(() => {
      this.timer--;
      if (this.timer <= 0) this.stopTimer();
    });
  }

  private stopTimer(): void {
    this.timerSubscription?.unsubscribe();
    this.isTimerActive = false;
  }
}
